/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `CloseProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface CloseProps {
    /**
     * A string representing the CSS class to be applied to the CloseIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#282828'
     */
    color1?: string;
    /**
     * A number or string representing the height of the CloseIcon element.
     *
     * @default 24
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Close'
     */
    testId?: string;
    /**
     * A number or string representing the height of the CloseIcon element.
     *
     * @default 24
     */
    width?: number | string;
}

/**
 * The `Close` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CloseProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CloseIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CloseIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CloseIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CloseComponent = <Close className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Close = ({
    className = '',
    color1 = '#282828',
    height = 24,
    ref,
    testId = 'Close',
    width = 24
}: CloseProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 24 24"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20m3-13-6 6m0-6 6 6" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
);

Close.displayName = 'Close';